import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs, Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { getLarkAuthUrl, loginWithPassword } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const Login: React.FC = () => {
  const [larkLoading, setLarkLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();
  const [form] = Form.useForm();

  const getRedirectPath = () => (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  // 如果已登录，跳转到之前的页面或首页
  useEffect(() => {
    if (isAuthenticated) {
      navigate(getRedirectPath(), { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  const handleLarkLogin = async () => {
    setLarkLoading(true);
    setError('');
    try {
      const authUrl = await getLarkAuthUrl(getRedirectPath());
      window.location.href = authUrl;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '获取登录地址失败');
      setLarkLoading(false);
    }
  };

  const handlePasswordLogin = async (values: { username: string; password: string }) => {
    setPasswordLoading(true);
    setError('');
    try {
      const result = await loginWithPassword(values.username, values.password);
      message.success('登录成功');
      // login() sets isAuthenticated=true, which triggers useEffect redirect
      login(result.token, result.user);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '登录失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  const tabItems = [
    {
      key: 'password',
      label: '账号登录',
      children: (
        <div style={{ paddingTop: 8 }}>
          <Form
            form={form}
            onFinish={handlePasswordLogin}
            size="large"
            autoComplete="off"
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: '请输入用户名' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
                placeholder="用户名"
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
                placeholder="密码"
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={passwordLoading}
                block
                style={{
                  height: 48,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 8,
                }}
              >
                登录
              </Button>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    {
      key: 'lark',
      label: 'Lark 登录',
      children: (
        <div style={{ paddingTop: 16 }}>
          <button
            className="lark-login-btn"
            onClick={handleLarkLogin}
            disabled={larkLoading}
          >
            <svg className="lark-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
            {larkLoading ? '跳转中...' : '使用 Lark 登录'}
          </button>
          <p className="login-tip">请使用公司 Lark 账号登录</p>
        </div>
      ),
    },
  ];

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <span className="logo-icon">🤖</span>
          <h1>BillyGo</h1>
        </div>
        <p className="login-subtitle">机器人管理系统</p>

        {error && <div className="login-error">{error}</div>}

        <Tabs
          defaultActiveKey="password"
          items={tabItems}
          centered
          onChange={() => setError('')}
        />
      </div>
    </div>
  );
};

export default Login;
